import { Head, usePage } from "@inertiajs/react";
import { t } from "@travily/i18n";
import type ServicesShow from "@travily/types/pages/ServicesShow";
import { TopBarMobile } from "@ui/components/TopBar/TopBarMobile";
import { configureDisplayCurrency } from "@ui/lib/format-price";
import type { ReactNode } from "react";
import { ShowView } from "@/components/ProductShowView/ShowView";
import { PageFrame } from "@/layouts/page-frame";
import PreviewLayout from "@/layouts/preview-layout";
import { buildShowViewProps } from "@/lib/build-show-view-props";
import { wrapReviewsInScroll } from "@/lib/reviews-scroll";
import type { SharedProps } from "@/types";

// Read-only buyer view of the owner's service, drafts included. Embedded in an iframe on
// the publish stage so creators see exactly what guests will see — the real page chrome,
// but rendered logged-out (the controller renders as a guest), and with navigation
// neutralised + analytics/chat omitted by PreviewLayout. CTAs are no-ops; the listing's
// own controls (carousel, tabs) stay interactive.
const noop = () => {};

const ServiceCreatorPreviewPage = ({ service, creator, reviews }: ServicesShow) => {
  const { currency, exchange_rates, locale } = usePage<SharedProps>().props;
  // The preview iframe boots cold (fresh module state), so the display-currency module
  // config is still null when buildShowViewProps formats prices below. PageFrame also
  // calls this, but only in its own later render pass — too late for the parent body's
  // price formatting, which would otherwise show the listing's source currency.
  configureDisplayCurrency(currency, exchange_rates, locale);

  const displayTitle = service.title ?? "";
  const showViewProps = buildShowViewProps("service", service, creator, reviews);

  return (
    <PageFrame stickyTopBar={false} mobileTopBar={<TopBarMobile onBack={noop} title={displayTitle} />}>
      <Head title={t("web.services.show.meta_title", { title: displayTitle })} />
      <ShowView
        {...showViewProps}
        favorited={false}
        onFavoriteToggle={noop}
        onPurchase={noop}
        onShare={noop}
        reviewsListWrapper={wrapReviewsInScroll}
      />
    </PageFrame>
  );
};

ServiceCreatorPreviewPage.layout = (page: ReactNode) => <PreviewLayout>{page}</PreviewLayout>;

export default ServiceCreatorPreviewPage;
